import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC, FocusEventHandler, FormEventHandler, KeyboardEventHandler } from 'react';
import { Input, List, ListItem } from '@chakra-ui/react';
import { useCombobox } from 'downshift';
import noop from 'lodash-es/noop';
import { nanoid } from 'nanoid';
import type { UseComboboxState, UseComboboxStateChangeOptions } from 'downshift';
import { toAddressInputValue } from '../../../utils/address-utils';
import { isServer } from '../../../utils/utils';
import styles from './Combobox.module.scss';
import type { AutocompleteAddress } from '../../../types/radar';

type SplitAddress = {
    formattedAddress: string;
    startAddress: string;
    matchAddress?: string | null;
    endAddress?: string | null;
};

const splitAddress = (address: AutocompleteAddress, searchTerm: string): SplitAddress => {
    const { formattedAddress = '' } = address;
    const startIndex = formattedAddress.toLowerCase().indexOf((searchTerm ?? '').toLowerCase());

    if (startIndex > -1) {
        const startAddress = formattedAddress.substring(0, startIndex);
        const matchAddress =
            formattedAddress.substring(startIndex, startIndex + searchTerm.length) ?? '';
        const endAddress = formattedAddress.substring(startIndex + searchTerm.length) ?? '';

        return {
            formattedAddress,
            startAddress,
            matchAddress,
            endAddress,
        };
    }

    return {
        formattedAddress,
        startAddress: formattedAddress,
    };
};

// Handshake timing is handled via double-RAF in onFocus; no extra delays needed
const createSuppressedFieldName = (): string => `f_${nanoid()}`;

export type ComboboxProps = {
    items: AutocompleteAddress[];
    handleInputChange: (value: string) => void;
    handleSelect: (address: AutocompleteAddress) => void;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
    inputProps: { id: string; name: string; autoComplete?: string; value?: string };
    variant?: string;
    useStreetAddressOnly?: boolean;
};

const getStreetAddress = (address: AutocompleteAddress | null): string => {
    if (!address) {
        return '';
    }
    const streetParts = [];
    if (address.number) {
        streetParts.push(address.number);
    }
    if (address.street) {
        streetParts.push(address.street);
    }
    return streetParts.join(' ').trim();
};

export const Combobox: FC<ComboboxProps> = ({
    items,
    handleInputChange,
    handleSelect,
    onKeyDown,
    inputProps,
    variant,
    useStreetAddressOnly = false,
}: ComboboxProps) => {
    const [isListBoxOpen, setIsListBoxOpen] = useState<boolean>(true);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [suppressBrowserAutocomplete, setSuppressBrowserAutocomplete] = useState<boolean>(false);
    const [autoCompleteToken, setAutoCompleteToken] = useState<string>('off');
    // Rotate a synthetic field name while suppression is active so Chrome
    // does not attach its autofill panel to our visible input.
    const [suppressedFieldName, setSuppressedFieldName] =
        useState<string>(createSuppressedFieldName);
    // Tracks last suppression state to know when to rotate the synthetic name
    const wasSuppressingRef = useRef<boolean>(false);
    // Stores the last value that came from a selection so refocus won't auto-open
    const selectedValueRef = useRef<string | null>(null);
    // Debounced one-time blur→focus handshake timeout id
    const idleHandshakeTimeoutRef = useRef<number | null>(null);
    const clearHandshakeTimeout = useCallback(() => {
        if (idleHandshakeTimeoutRef.current !== null) {
            window.clearTimeout(idleHandshakeTimeoutRef.current);
            idleHandshakeTimeoutRef.current = null;
        }
    }, []);

    const hasBlurredOnce = useRef<boolean>(false);
    const skipNextFocus = useRef<boolean>(false);
    const isOpenRef = useRef<boolean>(false);

    const stateReducer = useCallback(
        (
            state: UseComboboxState<AutocompleteAddress>,
            actionAndChanges: UseComboboxStateChangeOptions<AutocompleteAddress>,
        ): Partial<UseComboboxState<AutocompleteAddress>> => {
            const { type, changes } = actionAndChanges;

            const isSoftKeyboardOpen =
                !isServer &&
                window.visualViewport &&
                window.innerHeight > window.visualViewport?.height;

            switch (type) {
                case useCombobox.stateChangeTypes.InputBlur:
                    // If this blur is programmatic (our handshake), don't toggle suppression
                    if (!skipNextFocus.current) {
                        // Reset on real blur to allow browser autocomplete on next focus
                        setSuppressBrowserAutocomplete(false);
                        hasBlurredOnce.current = false;
                    }
                    return {
                        ...changes,
                        ...(isSoftKeyboardOpen ? { isOpen: true } : {}),
                    };
                default:
                    return { ...changes };
            }
        },
        [],
    );

    const {
        getInputProps,
        getMenuProps,
        getItemProps,
        isOpen,
        openMenu,
        closeMenu,
        highlightedIndex,
    } = useCombobox({
        items,
        inputValue: inputProps.value,
        onInputValueChange: (newValue) => {
            const { inputValue } = newValue;
            handleInputChange(inputValue || '');
        },
        onSelectedItemChange: ({ selectedItem }) => {
            const item = selectedItem as AutocompleteAddress | null;
            handleSelect(item as AutocompleteAddress);
            let selectedValue: string | null = null;
            if (item) {
                selectedValue = useStreetAddressOnly
                    ? getStreetAddress(item)
                    : toAddressInputValue(item);
            }
            selectedValueRef.current = selectedValue;
            // Close and keep closed until user types again
            closeMenu();
            setIsListBoxOpen(false);
        },
        itemToString: useStreetAddressOnly ? getStreetAddress : toAddressInputValue,
        stateReducer,
    });

    // Sync ref during render - MUST be synchronous for RAF callbacks
    isOpenRef.current = isOpen;

    // Compose Downshift navigation and user onKeyDown
    const {
        onKeyDown: downshiftOnKeyDown,
        onInput: downshiftOnInput,
        onBlur: downshiftOnBlur,
        ...downshiftInputProps
    } = getInputProps({
        ref: inputRef,
    });

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
        downshiftOnKeyDown?.(event);
        onKeyDown?.(event);
    };

    // No deferred timers; handshake runs immediately on focus

    const handleInput: FormEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            downshiftOnInput?.(event);
            if (!suppressBrowserAutocomplete) {
                setSuppressBrowserAutocomplete(true);
            }
            // User changed the field; allow reopening
            selectedValueRef.current = null;
        },
        [downshiftOnInput, suppressBrowserAutocomplete],
    );

    // Blur handler: real blurs close the list and reset suppression; programmatic
    // blurs from the handshake are ignored so we don't steal focus or flicker.
    const handleBlur: FocusEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            downshiftOnBlur?.(event);
            // Programmatic blur from our handshake: do nothing else
            if (skipNextFocus.current) {
                return;
            }
            // Real blur: cancel pending handshake, reset flags, and close menu
            clearHandshakeTimeout();
            hasBlurredOnce.current = false;
            setSuppressBrowserAutocomplete(false);
            closeMenu();
            setIsListBoxOpen(false);
        },
        [downshiftOnBlur, closeMenu, clearHandshakeTimeout],
    );

    // Focus handler: allow Chrome menu when empty; if user has typed (≥3) and
    // current value isn't exactly a previous selection, open Radar immediately.
    const handleFocus = useCallback(() => {
        const valueStr = inputProps.value ?? '';
        const trimmedLen = valueStr.trim().length;
        const hasValue = trimmedLen > 0;
        setSuppressBrowserAutocomplete(hasValue);

        if (skipNextFocus.current) {
            skipNextFocus.current = false;
            return;
        }

        // Open on focus only if there's user-entered text (not exactly the last selection)
        const isExactSelection =
            selectedValueRef.current !== null && valueStr === selectedValueRef.current;
        if (trimmedLen >= 3 && items.length > 0 && !isExactSelection) {
            openMenu();
            setIsListBoxOpen(true);
        }
    }, [inputProps.value, items.length, openMenu]);

    const inputPropsWithoutValue = useMemo<Omit<ComboboxProps['inputProps'], 'value'>>(() => {
        const { value, ...rest } = inputProps;
        return rest as Omit<ComboboxProps['inputProps'], 'value'>;
    }, [inputProps]);

    // Visibility effect: keeps Downshift and our list rendering in sync.
    // - opens on 3+ chars if focused; closes when fewer chars or no items
    // - never auto-opens when the input equals the last selected string
    useEffect(() => {
        const valueStr = inputProps.value ?? '';
        const canShow = valueStr.trim().length >= 3 && items.length > 0;
        const isExactSelection =
            selectedValueRef.current !== null && valueStr === selectedValueRef.current;

        if (isExactSelection) {
            if (isOpen) {
                closeMenu();
            }
            setIsListBoxOpen(false);
            return;
        }

        if (canShow && !isOpen && document.activeElement === inputRef.current) {
            openMenu();
        } else if (!canShow && isOpen) {
            closeMenu();
        }

        setIsListBoxOpen(canShow && isOpen);
    }, [inputProps.value, items.length, isOpen, openMenu, closeMenu]);

    // One-time handshake after brief idle when value >= 2 under suppression.
    // Purpose: nudge Chrome to hide its autocomplete panel without disrupting typing.
    // Guardrails:
    // - only once per focus session (hasBlurredOnce)
    // - skip if list is already open (prevents flicker)
    // - if user blurs elsewhere, we cancel the pending handshake
    useEffect(() => {
        const valueLen = inputProps.value?.length ?? 0;

        if (!suppressBrowserAutocomplete || hasBlurredOnce.current || valueLen < 2) {
            clearHandshakeTimeout();
            return noop;
        }

        const input = inputRef.current;
        if (!input || isOpen) {
            clearHandshakeTimeout();
            return noop;
        }

        clearHandshakeTimeout();

        idleHandshakeTimeoutRef.current = window.setTimeout(() => {
            if (suppressBrowserAutocomplete && !hasBlurredOnce.current && !isOpenRef.current) {
                const target = inputRef.current;
                if (target) {
                    // Mark programmatic cycle and blur
                    skipNextFocus.current = true;
                    hasBlurredOnce.current = true;
                    target.blur();
                    // Double RAF ensures Chrome forgets the field identity
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            // Only refocus if menu hasn't been opened by user action
                            if (skipNextFocus.current && !isOpenRef.current) {
                                target.focus({ preventScroll: true } as FocusOptions);
                            }
                            // Reset cycle flag after focus attempt
                            skipNextFocus.current = false;
                        });
                    });
                }
            }
        }, 120);

        return () => {
            clearHandshakeTimeout();
        };
    }, [suppressBrowserAutocomplete, inputProps.value, isOpen, clearHandshakeTimeout]);

    // When suppression first activates, rotate to a fresh field name so Chrome
    // forgets the original field association and does not attach its autofill panel.
    useEffect(() => {
        if (suppressBrowserAutocomplete && !wasSuppressingRef.current) {
            setSuppressedFieldName(createSuppressedFieldName());
        }

        wasSuppressingRef.current = suppressBrowserAutocomplete;
    }, [suppressBrowserAutocomplete]);

    // Update autocomplete token:
    // - while suppressed: random token to disable Chrome autofill/cache link
    // - otherwise: stable semantic section token so real autofill can work elsewhere
    useEffect(() => {
        if (suppressBrowserAutocomplete) {
            setAutoCompleteToken(`nope-${Math.random().toString(36).slice(2)}`);
        } else {
            // Use semantic tokens so legitimate browser autofill works elsewhere; stable per mount
            const section = createSuppressedFieldName();
            setAutoCompleteToken(`${section} shipping address-line1`);
        }
    }, [suppressBrowserAutocomplete]);

    return (
        <>
            {suppressBrowserAutocomplete && (
                <input
                    type="hidden"
                    name={inputProps.name}
                    value={inputProps.value || ''}
                    autoComplete="chrome-off"
                />
            )}
            <Input
                {...downshiftInputProps}
                {...inputPropsWithoutValue}
                name={suppressBrowserAutocomplete ? suppressedFieldName : inputProps.name}
                autoComplete={autoCompleteToken}
                type={suppressBrowserAutocomplete ? 'search' : 'text'}
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                inputMode={suppressBrowserAutocomplete ? 'search' : 'text'}
                css={{ borderRadius: '0.25rem 0.25rem 0 0' }}
                data-testid={inputProps.id}
                data-lpignore={suppressBrowserAutocomplete ? 'true' : undefined}
                data-form-type={suppressBrowserAutocomplete ? 'other' : undefined}
                data-1p-ignore={suppressBrowserAutocomplete ? 'true' : undefined}
                variant={variant}
                onKeyDown={handleKeyDown}
                onInput={handleInput}
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
            {isListBoxOpen && (
                <List
                    className={`w-full border border-t-0 border-solid ${styles.comboboxList}`}
                    variant={variant}
                    data-testid={`${inputProps.id}-list`}
                    {...getMenuProps({}, { suppressRefError: true })}
                >
                    {items.map((item, index) => {
                        const address = splitAddress(item, inputProps.value ?? '');

                        return (
                            <ListItem
                                padding={variant ? undefined : '0.5rem'}
                                backgroundColor={highlightedIndex === index ? '#F6F5F6' : ''}
                                // eslint-disable-next-line react/no-array-index-key
                                key={address.formattedAddress.replace(/\s/g, '') + index}
                                data-testid={`${inputProps.id}-list-item-${index}`}
                                {...getItemProps({ item, index })}
                            >
                                <span>
                                    {address.startAddress}
                                    {address.matchAddress ? (
                                        <>
                                            <strong>{address.matchAddress}</strong>
                                            {address.endAddress}
                                        </>
                                    ) : (
                                        ''
                                    )}
                                </span>
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </>
    );
};
